import { mode } from "@chakra-ui/theme-tools";

const buildDisabledState = (styles = {}) => ({
  cursor: "not-allowed",
  opacity: 0.6,
  boxShadow: "none",
  ...styles,
});

const createDisabledState = (styles = {}) => {
  const disabledState = buildDisabledState(styles);

  return {
    ...disabledState,
    _hover: disabledState,
    _active: disabledState,
    _focus: disabledState,
    "&:disabled, &[disabled], &[aria-disabled=true], &[data-disabled]": disabledState,
    "&:disabled:hover, &[disabled]:hover, &[aria-disabled=true]:hover, &[data-disabled]:hover":
      disabledState,
    "&:disabled:active, &[disabled]:active, &[aria-disabled=true]:active, &[data-disabled]:active":
      disabledState,
    "&:disabled:focus, &[disabled]:focus, &[aria-disabled=true]:focus, &[data-disabled]:focus":
      disabledState,
  };
};

export const buttonStyles = {
  components: {
    Button: {
      baseStyle: {
        borderRadius: "16px",
        boxShadow: "45px 76px 113px 7px rgba(112, 144, 176, 0.08)",
        transition: ".25s all ease",
        boxSizing: "border-box",
        _focus: {
          boxShadow: "none",
        },
        _active: {
          boxShadow: "none",
        },
        _disabled: createDisabledState(),
      },
      variants: {
        outline: () => ({
          borderRadius: "16px",
          _disabled: createDisabledState(),
        }),
        primary: (props) => ({
          bg: mode("cta.100", "brand.400")(props),
          color: "white",
          _focus: {
            bg: mode("cta.100", "brand.400")(props),
          },
          _active: {
            bg: mode("brand.600", "brand.400")(props),
          },
          _hover: {
            bg: mode("brand.600", "brand.400")(props),
          },
          _disabled: createDisabledState({
            bg: mode("cta.100", "brand.400")(props),
            color: "white",
          }),
        }),
        brand: (props) => ({
          bg: mode("brand.500", "brand.400")(props),
          color: "white",
          _focus: {
            bg: mode("brand.500", "brand.400")(props),
          },
          _active: {
            bg: mode("brand.500", "brand.400")(props),
          },
          _hover: {
            bg: mode("brand.600", "brand.400")(props),
          },
          _disabled: createDisabledState({
            bg: mode("brand.500", "brand.400")(props),
            color: "white",
          }),
        }),
        darkBrand: (props) => ({
          bg: mode("brand.900", "brand.400")(props),
          color: "white",
          _focus: {
            bg: mode("brand.900", "brand.400")(props),
          },
          _active: {
            bg: mode("brand.900", "brand.400")(props),
          },
          _hover: {
            bg: mode("brand.800", "brand.400")(props),
          },
          _disabled: createDisabledState({
            bg: mode("brand.900", "brand.400")(props),
            color: "white",
          }),
        }),
        lightBrand: (props) => ({
          bg: mode("#F2EFFF", "whiteAlpha.100")(props),
          color: mode("brand.500", "white")(props),
          _focus: {
            bg: mode("#F2EFFF", "whiteAlpha.100")(props),
          },
          _active: {
            bg: mode("secondaryGray.300", "whiteAlpha.100")(props),
          },
          _hover: {
            bg: mode("secondaryGray.400", "whiteAlpha.200")(props),
          },
          _disabled: createDisabledState({
            bg: mode("#F2EFFF", "whiteAlpha.100")(props),
            color: mode("brand.500", "white")(props),
          }),
        }),
        light: (props) => ({
          bg: mode("secondaryGray.400", "whiteAlpha.100")(props),
          color: mode("secondaryGray.900", "white")(props),
          _focus: {
            bg: mode("secondaryGray.300", "whiteAlpha.100")(props),
          },
          _active: {
            bg: mode("secondaryGray.300", "whiteAlpha.100")(props),
          },
          _hover: {
            bg: mode("secondaryGray.400", "whiteAlpha.200")(props),
          },
          _disabled: createDisabledState({
            bg: mode("secondaryGray.400", "whiteAlpha.100")(props),
            color: mode("secondaryGray.900", "white")(props),
          }),
        }),
        action: (props) => ({
          fontWeight: "500",
          borderRadius: "50px",
          bg: mode("secondaryGray.300", "brand.400")(props),
          color: mode("brand.500", "white")(props),
          _focus: {
            bg: mode("secondaryGray.300", "brand.400")(props),
          },
          _active: { bg: mode("secondaryGray.300", "brand.400")(props) },
          _hover: {
            bg: mode("secondaryGray.200", "brand.400")(props),
          },
          _disabled: createDisabledState({
            bg: mode("secondaryGray.300", "brand.400")(props),
            color: mode("brand.500", "white")(props),
          }),
        }),
        setup: (props) => ({
          fontWeight: "500",
          borderRadius: "50px",
          bg: mode("transparent", "brand.400")(props),
          border: mode("1px solid", "0px solid")(props),
          borderColor: mode("secondaryGray.400", "transparent")(props),
          color: mode("secondaryGray.900", "white")(props),
          _focus: {
            bg: mode("transparent", "brand.400")(props),
          },
          _active: { bg: mode("transparent", "brand.400")(props) },
          _hover: {
            bg: mode("secondaryGray.100", "brand.400")(props),
          },
          _disabled: createDisabledState({
            bg: mode("transparent", "brand.400")(props),
            border: mode("1px solid", "0px solid")(props),
            borderColor: mode("secondaryGray.400", "transparent")(props),
            color: mode("secondaryGray.900", "white")(props),
          }),
        }),
      },
    },
  },
};
